import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, ArrowRight, Zap, Shield, CalendarDays, Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { usePlanTranslator } from '../utils/i18nHelpers';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const planTr = usePlanTranslator();
  const [plans, setPlans] = useState([]);
  const [spacePrices, setSpacePrices] = useState([]);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, spacesRes] = await Promise.all([
          axios.get(`${API}/subscriptions/plans`),
          axios.get(`${API}/spaces`)
        ]);
        setPlans(plansRes.data);
        const seen = {};
        const deduped = [];
        for (const s of spacesRes.data) {
          const key = s.space_type;
          if (!seen[key]) {
            seen[key] = true;
            deduped.push({ space_id: s.space_id, name: s.name, h: s.price_hourly, d: s.price_daily, w: s.price_weekly });
          }
        }
        setSpacePrices(deduped);

        // Fetch subscription status
        if (token) {
          try {
            const statsRes = await axios.get(`${API}/dashboard/stats`, {
              headers: { Authorization: `Bearer ${token}` }, withCredentials: true
            });
            if (statsRes.data.subscription?.status === 'active') {
              setSubscription(statsRes.data.subscription);
            }
          } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
    };
    fetchData();
  }, [token]);

  const handleSubscribe = async (planId) => {
    if (!user) {
      navigate('/auth?mode=register');
      return;
    }

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/payments/subscribe`, {
        plan_id: planId,
        origin_url: window.location.origin
      }, { headers, withCredentials: true });

      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t('subscriptions.subscribeError'));
    }
  };

  const formatDate = (isoStr) => {
    try {
      const locale = i18n.language?.startsWith('nl') ? 'nl-BE' : i18n.language?.startsWith('en') ? 'en-US' : 'fr-BE';
      return new Date(isoStr).toLocaleDateString(locale, { day: '2-digit', month: 'long', year: 'numeric' });
    } catch {
      return isoStr?.slice(0, 10) || '';
    }
  };

  const hasActiveSubscription = !!subscription;

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="subscription-page">
      <SEO titleKey="seo.subscriptions.title" descriptionKey="seo.subscriptions.description" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

        {/* Active subscription banner */}
        {hasActiveSubscription ? (
          <div className="text-center mb-14" data-testid="active-subscription-header">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#1A3C34] mb-6">
              <Star className="w-8 h-8 text-[#E8D44D]" />
            </div>
            <div className="bg-[#1A3C34] rounded-xl p-8 max-w-2xl mx-auto shadow-lg">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-[#E8D44D]" />
                <span className="text-[#E8D44D] text-sm font-semibold uppercase tracking-wider">{t('subscriptions.activeSubscriber')}</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white mt-2">
                {t(`subscriptions.planNames.${subscription.plan_id}`, { defaultValue: subscription.plan_name })}
              </h1>
              <div className="mt-4 inline-block bg-[#E8D44D]/15 border border-[#E8D44D]/30 rounded-lg px-6 py-3">
                <p className="text-[#E8D44D] text-lg font-semibold">
                  {t('subscriptions.activeUntil', { date: formatDate(subscription.expires_at) })}
                </p>
              </div>
              <div className="mt-5 flex items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-1.5 text-white/70">
                  <CalendarDays className="w-4 h-4" />
                  {t('subscriptions.starts')} : <span className="text-white font-medium">{formatDate(subscription.starts_at)}</span>
                </div>
                <div className="flex items-center gap-1.5 text-white/70">
                  <CalendarDays className="w-4 h-4" />
                  {t('subscriptions.ends')} : <span className="text-white font-medium">{formatDate(subscription.expires_at)}</span>
                </div>
              </div>
              <Badge className="mt-4 bg-[#E8D44D]/20 text-[#E8D44D] border-[#E8D44D]/30 text-sm px-4 py-1">
                -{subscription.discount_percent}% {t('subscriptions.discountAll')}
              </Badge>
            </div>
          </div>
        ) : (
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C77D63]/10 border border-[#C77D63]/20 rounded-full text-sm font-semibold text-[#C77D63] mb-6">
              <Zap className="w-4 h-4" />
              {t('subscriptions.saveUpTo')}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#1A3C34] tracking-tight">
              {t('subscriptions.title')}
            </h1>
            <p className="mt-4 text-base text-[#737373] max-w-md mx-auto leading-relaxed font-body">
              {t('subscriptions.subtitle')}
            </p>
          </div>
        )}

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {plans.map((plan) => {
            const isAnnual = plan.duration === 'annual';
            const totalPrice = isAnnual ? Math.round(plan.price_monthly * 12) : null;
            const isCurrentPlan = hasActiveSubscription && subscription.plan_id === plan.plan_id;
            return (
              <div
                key={plan.plan_id}
                data-testid={`plan-card-${plan.plan_id}`}
                className={`relative bg-white border rounded-xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ${
                  isCurrentPlan
                    ? 'border-[#E8D44D] ring-2 ring-[#E8D44D]/50'
                    : isAnnual
                    ? 'border-[#1A3C34] ring-1 ring-[#1A3C34]'
                    : 'border-[#E2E2DF] hover:border-[#1A3C34]/30'
                } ${hasActiveSubscription && !isCurrentPlan ? 'opacity-50' : ''}`}
              >
                {isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#E8D44D] text-[#1A3C34] text-xs px-3 py-1 font-semibold">
                      <Star className="w-3 h-3 mr-1" /> {t('subscriptions.yourSubscription')}
                    </Badge>
                  </div>
                )}
                {!isCurrentPlan && isAnnual && !hasActiveSubscription && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#1A3C34] text-white text-xs px-3 py-1">
                      {t('subscriptions.recommended')}
                    </Badge>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="font-heading text-xl font-semibold text-[#1A3C34]">
                    {planTr.name(plan)}
                  </h3>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="font-mono text-4xl font-semibold text-[#1A3C34]">{plan.price_monthly}</span>
                    <span className="text-sm text-[#737373]">EUR{t('subscriptions.perMonth')} HT</span>
                  </div>
                  <div className="text-[10px] text-[#C77D63] font-semibold mt-0.5" data-testid={`plan-htva-${plan.plan_id}`}>
                    ★ {t('subscriptions.htvaNotice', { defaultValue: 'HTVA · TVA 21% en sus' })}
                  </div>
                  {totalPrice && (
                    <p className="mt-1 text-xs text-[#737373]">
                      {t('subscriptions.perYearTotal', { total: totalPrice })}
                    </p>
                  )}
                  <Badge className="mt-3 bg-green-50 text-green-700 border-green-200">
                    -{plan.discount_percent}% {t('subscriptions.discountOnBookings')}
                  </Badge>
                </div>

                <ul className="space-y-3 mb-8">
                  {planTr.features(plan).map((feat, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#1A3C34]">
                      <Check className="w-4 h-4 text-[#4CAF50] mt-0.5 flex-shrink-0" />
                      {feat}
                    </li>
                  ))}
                </ul>

                {isCurrentPlan ? (
                  <div className="w-full h-12 rounded-md font-medium bg-[#E8D44D]/10 border border-[#E8D44D]/30 flex items-center justify-center text-[#1A3C34] text-sm">
                    <Check className="w-4 h-4 mr-2 text-[#E8D44D]" />
                    {t('subscriptions.currentActive')}
                  </div>
                ) : hasActiveSubscription ? (
                  <div className="w-full h-12 rounded-md font-medium bg-gray-100 flex items-center justify-center text-[#737373] text-sm">
                    {t('subscriptions.subscriptionInProgress')}
                  </div>
                ) : (
                  <Button
                    data-testid={`subscribe-btn-${plan.plan_id}`}
                    onClick={() => handleSubscribe(plan.plan_id)}
                    className={`w-full h-12 rounded-lg font-bold text-sm ${
                      isAnnual
                        ? 'bg-[#C77D63] text-white hover:bg-[#b06a51] shadow-md shadow-[#C77D63]/20'
                        : 'bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90'
                    }`}
                  >
                    {t('subscriptions.subscribeBtn')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Most-expensive room example — visualises the savings from each subscription tier */}
        <div className="mt-16" data-testid="pricing-example">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[#1A3C34] mb-2 text-center">
            {t('subscriptions.exampleTitle', { defaultValue: 'Exemple — Grande Salle (notre tarif le plus haut)' })}
          </h2>
          <p className="text-sm text-[#737373] text-center mb-6 max-w-2xl mx-auto">
            {t('subscriptions.exampleSubtitle', { defaultValue: 'Comparez ce que vous payez réellement pour la Grande Salle, avec ou sans abonnement.' })}
          </p>
          <div className="bg-[#C77D63]/10 border border-[#C77D63]/25 rounded-md px-3 py-2 mb-5 flex items-center gap-1.5 max-w-3xl mx-auto" data-testid="subscriptions-table-htva-notice">
            <span className="text-[#C77D63]">★</span>
            <span className="text-xs font-semibold text-[#C77D63]">
              {t('subscriptions.tableHtvaNotice', { defaultValue: 'Tous les prix affichés ci-dessous sont HTVA · TVA 21% ajoutée à l\'addition' })}
            </span>
          </div>
          <PricingExampleCard plans={plans} t={t} />
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate('/tarifs')}
              data-testid="see-all-prices-btn"
              className="text-sm font-semibold text-[#1A3C34] hover:text-[#C77D63] underline underline-offset-4"
            >
              {t('subscriptions.seeAllPrices', { defaultValue: 'Voir tous les tarifs (toutes les salles, toutes les durées) →' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PricingExampleCard({ plans, t }) {
  const [grandeSalle, setGrandeSalle] = React.useState(null);
  React.useEffect(() => {
    axios.get(`${API}/spaces`).then((r) => {
      // "Most expensive" = highest price_hourly. Default to grande-salle.
      const list = r.data || [];
      const top = list.reduce((acc, s) => (Number(s.price_hourly || 0) > Number(acc?.price_hourly || 0) ? s : acc), list[0]);
      setGrandeSalle(top);
    }).catch(() => {});
  }, []);

  if (!grandeSalle) return null;
  const solo = plans.find((p) => p.plan_id === 'solo');
  const pro = plans.find((p) => p.plan_id === 'pro');
  const soloPct = Number(solo?.discount_percent || 15);
  const proPct = Number(pro?.discount_percent || 30);
  const apply = (p, pct) => Math.round(Number(p || 0) * (1 - pct / 100) * 100) / 100;
  const rows = [
    { key: 'h', label: t('subscriptions.exHour', { defaultValue: 'Heure' }), value: grandeSalle.price_hourly },
    { key: 'hd', label: t('subscriptions.exHalfDay', { defaultValue: 'Demi-journée (4h)' }), value: grandeSalle.price_half_day },
    { key: 'd', label: t('subscriptions.exDay', { defaultValue: 'Jour (06h–22h)' }), value: grandeSalle.price_daily },
    { key: 'w', label: t('subscriptions.exWeek', { defaultValue: 'Semaine' }), value: grandeSalle.price_weekly },
  ].filter((r) => Number(r.value) > 0);

  return (
    <div className="bg-white border-2 border-[#1A3C34] rounded-2xl p-6 sm:p-8 shadow-sm max-w-3xl mx-auto" data-testid="grande-salle-example">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-[#E2E2DF] text-[#737373]">
              <th className="text-left py-2 px-2 font-medium">{t('subscriptions.exDuration', { defaultValue: 'Durée' })}</th>
              <th className="text-right py-2 px-2 font-medium">{t('subscriptions.exNoSub', { defaultValue: 'Sans abonnement' })}</th>
              <th className="text-right py-2 px-2 font-medium">Pass Solo <span className="text-[#C77D63]">(-{soloPct}%)</span></th>
              <th className="text-right py-2 px-2 font-medium">Pass Pro <span className="text-[#C77D63]">(-{proPct}%)</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b border-[#F0F0EE] hover:bg-[#FAFAF8] transition-colors">
                <td className="py-3 px-2 text-[#1A3C34] font-medium">{r.label}</td>
                <td className="py-3 px-2 text-right font-mono text-[#1A3C34]">{Number(r.value).toFixed(2)} EUR</td>
                <td className="py-3 px-2 text-right font-mono text-[#C77D63] font-semibold">{apply(r.value, soloPct).toFixed(2)} EUR</td>
                <td className="py-3 px-2 text-right font-mono text-[#C77D63] font-semibold">{apply(r.value, proPct).toFixed(2)} EUR</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
